import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBasket, Truck, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SpiceIndicator } from "@/components/SpiceIndicator";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { money, PICKUP_ADDRESS, CATEGORY_LABELS } from "@/config";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const { addItem, setOpen } = useCart();
  const reduce = useReducedMotion();

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setQty(1);
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        const def = data.variants.find((v) => v.id === "250ml") || data.variants[0];
        setVariantId(def?.id);
        api.get("/products").then((r) =>
          setRelated(r.data.filter((p) => p.slug !== slug && p.category === data.category).slice(0, 4))
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const variant = useMemo(
    () => product?.variants.find((v) => v.id === variantId),
    [product, variantId]
  );

  if (notFound)
    return (
      <div className="mx-auto max-w-7xl px-4 py-32 text-center" data-testid="product-not-found">
        <p className="font-script font-semibold text-4xl text-clay">Esta salsa se nos acabó del reparto...</p>
        <Button asChild className="mt-6 rounded-full bg-habanero text-white hover:bg-habanero-dark">
          <Link to="/tienda">Volver a la tienda</Link>
        </Button>
      </div>
    );

  if (!product)
    return <p className="py-32 text-center font-script font-semibold text-3xl text-clay">Destapando el frasco...</p>;

  const handleAdd = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    addItem(product, variant, qty, rect);
    toast.success(`${qty} × ${product.name} agregado`, {
      action: { label: "Ver carrito", onClick: () => setOpen(true) },
    });
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: [product.image],
    description: product.short,
    brand: { "@type": "Brand", name: "SoyDiVa" },
    offers: product.variants.map((v) => ({
      "@type": "Offer",
      priceCurrency: "MXN",
      price: v.price,
      availability: v.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    })),
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8" data-testid="product-detail-page">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Link to="/tienda" className="flex items-center gap-1 text-sm font-bold text-clay transition-colors hover:text-habanero" data-testid="product-back-link">
        <ArrowLeft size={16} /> Volver a la tienda
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        <motion.div
          onMouseMove={(e) => {
            if (reduce) return;
            const r = e.currentTarget.getBoundingClientRect();
            mx.set((e.clientX - r.left) / r.width - 0.5);
            my.set((e.clientY - r.top) / r.height - 0.5);
          }}
          onMouseLeave={() => { mx.set(0); my.set(0); }}
          style={reduce ? {} : { rotateX, rotateY, transformPerspective: 1000 }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="lg:sticky lg:top-24 lg:self-start"
        >
          <div className="flex items-center justify-center rounded-3xl border border-sand bg-surface p-10">
            <img
              src={product.image}
              alt={`Frasco de ${product.name} — SoyDiVa`}
              className="max-h-[480px] w-auto object-contain jar-shadow"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <p className="font-script font-semibold text-2xl text-orange">
            {CATEGORY_LABELS[product.category] || "Salsa artesanal"}
          </p>
          <h1 className="mt-2 font-script text-4xl font-semibold text-cocoa sm:text-5xl" data-testid="product-name">
            {product.name}
          </h1>
          {product.category !== "kit" && (
            <div className="mt-4">
              <SpiceIndicator level={product.spice_level} interactive size={20} />
            </div>
          )}
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.p
              key={variant?.id}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-5 font-display text-4xl font-extrabold text-habanero"
              data-testid="product-price"
            >
              {variant ? money(variant.price) : ""}
            </motion.p>
          </AnimatePresence>
          <p className="mt-6 leading-relaxed text-clay">{product.description}</p>

          {product.category !== "kit" && (
          <div className="mt-8">
            <p className="text-sm font-bold uppercase tracking-wide text-cocoa">Presentación</p>
            <RadioGroup value={variantId} onValueChange={setVariantId} className="mt-3 flex flex-wrap gap-3">
              {product.variants.map((v) => (
                <div key={v.id}>
                  <RadioGroupItem value={v.id} id={`v-${v.id}`} className="peer sr-only" disabled={v.stock <= 0} data-testid={`variant-radio-${v.id}`} />
                  <Label
                    htmlFor={`v-${v.id}`}
                    data-testid={`variant-label-${v.id}`}
                    className={`flex cursor-pointer flex-col rounded-xl border px-5 py-3 transition-colors peer-data-[state=checked]:border-habanero peer-data-[state=checked]:bg-habanero/5 ${
                      v.stock <= 0 ? "cursor-not-allowed opacity-40" : "border-sand hover:border-habanero"
                    }`}
                  >
                    <span className="text-sm font-bold text-cocoa">{v.label}</span>
                    <span className="text-sm font-bold text-habanero">{money(v.price)}</span>
                    <span className="text-[11px] text-clay">{v.stock > 0 ? `${v.stock} disponibles` : "Agotado"}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 rounded-full border border-sand px-2 py-1">
              <button data-testid="qty-minus" aria-label="Disminuir cantidad" className="p-2 text-clay hover:text-habanero" onClick={() => setQty((q) => Math.max(1, q - 1))}>
                <Minus size={16} />
              </button>
              <span className="min-w-6 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" aria-label="Aumentar cantidad" className="p-2 text-clay hover:text-habanero" onClick={() => setQty((q) => Math.min(variant?.stock || 50, q + 1))}>
                <Plus size={16} />
              </button>
            </div>
            <Button
              data-testid="product-add-to-cart"
              disabled={!variant || variant.stock <= 0}
              onClick={handleAdd}
              className="flex-1 rounded-full bg-habanero py-6 text-base font-bold text-white transition-colors hover:bg-habanero-dark disabled:bg-sand sm:flex-none sm:px-10"
            >
              <ShoppingBasket className="mr-2" size={18} />
              Agregar — {variant ? money(variant.price * qty) : ""}
            </Button>
          </div>

          <Separator className="my-8 bg-sand" />

          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-cocoa">Ingredientes</p>
            <ul className="mt-3 flex flex-wrap gap-2" data-testid="product-ingredients">
              {product.ingredients.map((ing) => (
                <li key={ing} className="rounded-full bg-surface px-4 py-1.5 text-xs font-semibold text-cocoa">
                  {ing}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 grid gap-3 text-sm sm:grid-cols-2">
            <div className="flex items-start gap-3 rounded-xl border border-sand bg-white p-4">
              <Truck size={18} className="mt-0.5 text-leaf" />
              <div>
                <p className="font-bold text-cocoa">Envío a domicilio</p>
                <p className="text-xs text-clay">$60 local Mérida · $350 nacional</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-sand bg-white p-4">
              <MapPin size={18} className="mt-0.5 text-habanero" />
              <div>
                <p className="font-bold text-cocoa">Pick up en Mérida</p>
                <p className="text-xs text-clay">{PICKUP_ADDRESS}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-script text-2xl font-semibold text-cocoa sm:text-3xl">También te puede antojar</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
